/**
 * <ry> Transform Wrapper
 *
 * Allows clean, unprefixed markup inside <ry> tags.
 * Converts <accordion> to <ry-accordion>, etc.
 *
 * Usage:
 * <ry>
 *   <accordion>
 *     <accordion-item title="Foo">Content</accordion-item>
 *   </accordion>
 * </ry>
 *
 * Or with <template ry>:
 * <template ry>
 *   <accordion>...</accordion>
 * </template>
 *
 * Both prefixed and unprefixed work together:
 * <ry>
 *   <accordion>
 *     <ry-accordion-item>Already prefixed, unchanged</ry-accordion-item>
 *     <accordion-item>Gets transformed</accordion-item>
 *   </accordion>
 * </ry>
 */

// All component names that can be transformed
const RY_COMPONENTS: readonly string[] = [
  // Layout
  'page', 'header', 'main', 'footer', 'section', 'aside',
  'grid', 'stack', 'cluster', 'split', 'center',
  'nav', 'logo', 'actions',
  // Components
  'accordion', 'accordion-item',
  'tabs', 'tab',
  'modal',
  'dropdown', 'menu', 'menu-item',
  'drawer',
  'toast',
  'alert',
  'card',
  'button',
  'badge',
  'field',
  'switch',
  'tooltip',
  'select', 'option',
  'theme-toggle',
  'divider',
  'icon',
  'example',
  'toggle-button',
  'slider',
  'knob',
  'number-select',
  'color-picker',
  'color-input',
  'gradient-picker',
  'tree',
  'tree-item',
  'tag',
  'tag-input',
  'hero',
  'stat',
  'feature',
  'feature-grid',
  'pricing',
  'pricing-card',
  'carousel',
  'combobox',
  'theme-panel',
  'testimonial',
  'heading',
  'search-list',
  'search-item',
  'search-group',
  'logo-bar'
] as const;

// Build regex once - matches tags not already prefixed
const TRANSFORM_REGEX = new RegExp(
  `<(/?)(${RY_COMPONENTS.join('|')})(\\s|>|/)`,
  'g'
);

/**
 * Transform unprefixed markup to ry- prefixed
 */
export function transform(html: string): string {
  return html.replace(TRANSFORM_REGEX, '<$1ry-$2$3');
}

/**
 * Process all <ry> wrappers and <template ry> blocks
 */
export function processTransforms(): void {
  // Process <ry> elements
  document.querySelectorAll('ry').forEach(el => {
    const transformed = transform(el.innerHTML);
    const template = document.createElement('template');
    template.innerHTML = transformed;
    el.replaceWith(template.content);
  });

  // Process <template ry> elements
  document.querySelectorAll('template[ry]').forEach(el => {
    const transformed = transform((el as HTMLTemplateElement).innerHTML);
    const template = document.createElement('template');
    template.innerHTML = transformed;
    el.replaceWith(template.content);
  });
}

// Run transforms when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', processTransforms);
} else {
  // DOM already loaded, run immediately
  processTransforms();
}
